// Account → Users: who can sign in to this company's console.
// The owner (the master user who signed up) and admins invite people by email,
// choose Admin or Member, resend or cancel invites, change roles and remove
// users. Members see the list read-only. Backed by 100-api functions/team.
import { useEffect, useState, type FormEvent } from "react";
import { MailPlus, RotateCw, Trash2, X } from "lucide-react";
import { useApi, type Team, type TeamMember, type TeamRole } from "../api";
import { ConfirmDialog } from "../components/ConfirmDialog";
import "../widgets.css";

const ROLE_LABELS: Record<TeamRole, string> = { owner: "Owner", admin: "Admin", member: "Member" };
const ROLE_HELP: Record<TeamRole, string> = {
  owner: "Signed the company up. Manages users and billing; can't be removed.",
  admin: "Everything, including users and billing.",
  member: "Components, leads, quotes and analytics. Not users or billing.",
};

const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

export function UsersPage() {
  const api = useApi();
  const [team, setTeam] = useState<Team | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [busy, setBusy] = useState<string | null>(null); // which action is running
  const [removing, setRemoving] = useState<TeamMember | null>(null);

  useEffect(() => {
    api
      .getTeam()
      .then(setTeam)
      .catch((e: Error) => setError(e.message));
  }, [api]);

  const manage = team ? team.me.role === "owner" || team.me.role === "admin" : false;
  const used = team ? team.members.length + team.invites.length : 0;

  /** Run a change, then show the team the API sends back (or its error). */
  async function run(key: string, action: () => Promise<Team>, done?: string) {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      setTeam(await action());
      if (done) setNotice(done);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function invite(e: FormEvent) {
    e.preventDefault();
    const address = email.trim();
    if (!address) return;
    if (await run("invite", () => api.inviteUser(address, role), `Invite sent to ${address}.`)) setEmail("");
  }

  return (
    <div className="page users-page">
      <div className="page-head">
        <h1>Users</h1>
        <p>Who can sign in to your xenquote console. Invite people by email: they choose their own password.</p>
      </div>
      {error && <div className="flash flash-danger" role="alert">{error}</div>}
      {notice && <div className="flash flash-success" role="status">{notice}</div>}

      {!team ? (
        !error && <p className="muted">Loading…</p>
      ) : (
        <>
          {/* Invite someone (owner and admins) */}
          {manage && (
            <section className="card users-invite">
              <h2>Invite a user</h2>
              <form className="users-invite-form" onSubmit={(e) => void invite(e)}>
                <input
                  className="input"
                  type="email"
                  placeholder="name@yourcompany.co.uk"
                  aria-label="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy === "invite" || used >= team.maxUsers}
                  required
                />
                <select className="input users-role-select" aria-label="Role" value={role} onChange={(e) => setRole(e.target.value as "admin" | "member")}>
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <button type="submit" className="btn btn-primary" disabled={busy === "invite" || !email.trim() || used >= team.maxUsers}>
                  <MailPlus size={16} aria-hidden="true" /> {busy === "invite" ? "Sending…" : "Send invite"}
                </button>
              </form>
              <p className="muted users-hint">
                {ROLE_HELP[role]} The link in the email works for 7 days. {used}/{team.maxUsers} users, including invites.
              </p>
            </section>
          )}

          {/* Everyone who can sign in */}
          <section className="card">
            <h2>People</h2>
            <div className="table-scroll">
              <table className="users-table">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Email</th>
                    <th scope="col">Role</th>
                    <th scope="col">Joined</th>
                    {manage && <th scope="col" aria-label="Actions" />}
                  </tr>
                </thead>
                <tbody>
                  {team.members.map((m) => {
                    const you = m.sub === team.me.sub;
                    // The owner and yourself can't be changed here
                    const editable = manage && !you && m.role !== "owner";
                    return (
                      <tr key={m.sub}>
                        <td>
                          <span className="users-name">
                            {m.pictureUrl ? <img className="users-avatar" src={m.pictureUrl} alt="" referrerPolicy="no-referrer" /> : <span className="users-avatar users-initial">{(m.name || m.email).charAt(0).toUpperCase()}</span>}
                            {m.name || <span className="muted">No name yet</span>}
                            {you && <span className="users-you">You</span>}
                          </span>
                        </td>
                        <td>{m.email}</td>
                        <td>
                          {editable ? (
                            <select
                              className="input users-role-select"
                              aria-label={`Role for ${m.email}`}
                              value={m.role}
                              disabled={busy === `role-${m.sub}`}
                              onChange={(e) => void run(`role-${m.sub}`, () => api.setUserRole(m.sub, e.target.value as "admin" | "member"), `${m.name || m.email} is now ${e.target.value === "admin" ? "an admin" : "a member"}.`)}
                            >
                              <option value="member">Member</option>
                              <option value="admin">Admin</option>
                            </select>
                          ) : (
                            <span className={`users-role is-${m.role}`} title={ROLE_HELP[m.role]}>
                              {ROLE_LABELS[m.role]}
                            </span>
                          )}
                        </td>
                        <td className="muted">{date(m.joinedAt)}</td>
                        {manage && (
                          <td className="users-actions">
                            {editable && (
                              <button type="button" className="link-button danger" onClick={() => setRemoving(m)}>
                                <Trash2 size={14} aria-hidden="true" /> Remove
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Invites not accepted yet */}
          {team.invites.length > 0 && (
            <section className="card">
              <h2>Pending invites</h2>
              <div className="table-scroll">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th scope="col">Email</th>
                      <th scope="col">Role</th>
                      <th scope="col">Invited</th>
                      <th scope="col">Status</th>
                      {manage && <th scope="col" aria-label="Actions" />}
                    </tr>
                  </thead>
                  <tbody>
                    {team.invites.map((i) => (
                      <tr key={i.email}>
                        <td>{i.email}</td>
                        <td>
                          <span className={`users-role is-${i.role}`}>{ROLE_LABELS[i.role]}</span>
                        </td>
                        <td className="muted">
                          {date(i.invitedAt)}
                          {i.invitedBy && ` by ${i.invitedBy}`}
                        </td>
                        <td>{i.expired ? <span className="users-expired">Expired</span> : <span className="muted">Until {date(i.expiresAt)}</span>}</td>
                        {manage && (
                          <td className="users-actions">
                            <button type="button" className="link-button" disabled={!!busy} onClick={() => void run(`resend-${i.email}`, () => api.resendInvite(i.email), `Invite sent again to ${i.email}.`)}>
                              <RotateCw size={14} aria-hidden="true" /> Resend
                            </button>
                            <button type="button" className="link-button danger" disabled={!!busy} onClick={() => void run(`revoke-${i.email}`, () => api.revokeInvite(i.email), `Invite to ${i.email} cancelled.`)}>
                              <X size={14} aria-hidden="true" /> Cancel
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* What each role can do */}
          <section className="card users-roles">
            <h2>Roles</h2>
            <dl>
              {(Object.keys(ROLE_LABELS) as TeamRole[]).map((r) => (
                <div key={r}>
                  <dt>
                    <span className={`users-role is-${r}`}>{ROLE_LABELS[r]}</span>
                  </dt>
                  <dd>{ROLE_HELP[r]}</dd>
                </div>
              ))}
            </dl>
          </section>
        </>
      )}

      <ConfirmDialog
        open={!!removing}
        title={`Remove ${removing?.name || removing?.email || "this user"}?`}
        confirmLabel="Remove user"
        busy={busy === "remove"}
        onConfirm={async () => {
          if (!removing) return;
          const who = removing;
          if (await run("remove", () => api.removeUser(who.sub), `${who.name || who.email} has been removed.`)) setRemoving(null);
        }}
        onCancel={() => setRemoving(null)}
      >
        <p>They'll be signed out and their login deleted. Everything they made stays with your company. You can invite them again later.</p>
      </ConfirmDialog>
    </div>
  );
}
